<!--
  CnWidgetWrapper — Container shell around a dashboard widget.

  Provides header (icon + title), scrollable content area, and optional
  footer with action buttons. Applies style configuration for borders,
  backgrounds, and padding. The built-in overflow Actions menu (Refresh /
  Documentation / Request a feature) is delegated to the shared
  CnActionsMenu so widgets and page surfaces stay in lockstep.
-->
<template>
	<div
		class="cn-widget-wrapper"
		:class="{
			'cn-widget-wrapper--borderless': noBorder,
			'cn-widget-wrapper--flush': noPadding,
			'cn-widget-wrapper--chromeless': chromeless,
			'cn-widget-wrapper--nc-dashboard': chrome === 'nc-dashboard',
		}"
		:style="wrapperStyles">
		<!-- Header.
		     Rendered when a title earns it, or when controls that are actually
		     on screen need a home. Three conditions rather than one, and each of
		     the three is a bug this component has already shipped: gating on the
		     title alone took the Save button for an inline edit away along with
		     the doubled title; reading `showActions`, which defaults to true,
		     handed a header to every headerless KPI tile; and reading the
		     `actions` slot's mere PRESENCE gave a chromeless panel a 59px band
		     holding nothing, because `CnObjectDataWidget` provides that template
		     always and fills it only while an edit is unsaved.
		     `headerIsWorthIt()` carries the reasoning. -->
		<div v-if="headerIsWorthIt()"
			class="cn-widget-wrapper__header"
			:class="{ 'cn-widget-wrapper__header--actions-only': !titleVisible }"
			:style="[headerStyles, titleIconStyle]">
			<!-- Title icon — left: rendered before the title group. Moved
			     INSIDE header-left so the header's `space-between` cannot pull
			     it away from the title it belongs to; header-left's `gap` is
			     what now separates icon from title (they used to touch). -->
			<div v-if="titleVisible" class="cn-widget-wrapper__header-left">
				<div v-if="$slots['title-icon'] && titleIconPosition === 'left'"
					class="cn-widget-wrapper__title-icon">
					<slot name="title-icon" />
				</div>
				<img
					v-if="iconUrl"
					:src="iconUrl"
					:alt="resolvedTitle"
					class="cn-widget-wrapper__icon">
				<span
					v-else-if="iconClass"
					:class="iconClass"
					class="cn-widget-wrapper__icon" />
				<h3 :id="titleId" class="cn-widget-wrapper__title">
					{{ resolvedTitle }}
				</h3>
				<!-- @slot title-meta Rendered inside the header's left group,
				     after the title and before the spacer. Use for small
				     contextual chips (e.g. a date-range badge on chart
				     widgets). Kept inside header-left so the chip sits next
				     to the title rather than floating right next to the
				     actions menu. -->
				<div v-if="$slots['title-meta']" class="cn-widget-wrapper__title-meta">
					<slot name="title-meta" />
				</div>
			</div>
			<!-- `showActions` gates the overflow MENU, not the slot beside it.
			     It used to gate both, so a caller who switched the menu off
			     also lost the Save button for an inline edit, silently. -->
			<div v-if="actionsAreVisible()" class="cn-widget-wrapper__actions">
				<!-- @slot actions Custom action buttons rendered before the
				     built-in overflow menu. -->
				<slot name="actions" />
				<CnActionsMenu
					v-if="showActions"
					:showRefresh="effectiveShowRefresh"
					:showRequestFeature="effectiveShowRequestFeature"
					:showReportBug="showReportBug"
					:showDocumentation="showDocumentation"
					:documentationUrl="documentationUrl"
					:docsAnchor="docsAnchor"
					:reportBugUrl="reportBugUrl"
					:documentationLabel="documentationLabel"
					:refreshLabel="refreshLabel"
					:requestFeatureLabel="requestFeatureLabel"
					:actionsMenuLabel="actionsMenuLabel"
					:refreshing="refreshing"
					:widgetId="resolvedWidgetId"
					:title="displayTitle"
					:surface="`widget:${resolvedWidgetId}`"
					:specRef="specRef"
					refreshChannel="cn:widget:refresh"
					testidBase="cn-widget-wrapper"
					@refresh="onActionsRefresh"
					@requestFeature="onActionsRequestFeature">
					<!-- @slot action-items Additional NcActionButton-family
					     items rendered inside the overflow menu, after the
					     built-in Refresh / Documentation / Request-a-feature
					     group. -->
					<template v-if="hasActionItemsSlot" #action-items>
						<slot name="action-items" />
					</template>
				</CnActionsMenu>
			</div>
			<!-- Title icon — right: rendered after actions, far right -->
			<div v-if="$slots['title-icon'] && titleIconPosition === 'right'"
				class="cn-widget-wrapper__title-icon">
				<slot name="title-icon" />
			</div>
		</div>

		<!-- Headerless meta (e.g. a KPI date chip on a flush card). When the
		     header is hidden but a `title-meta` chip is provided, float it in the
		     top-right corner over the content instead of dropping it — so a
		     compact flush KPI tile can carry a date-range chip without growing a
		     full header bar. -->
		<div
			v-if="!titleVisible && $slots['title-meta']"
			class="cn-widget-wrapper__floating-meta">
			<slot name="title-meta" />
		</div>

		<!-- Content -->
		<!--
		  The content area is `overflow: auto`, so it can become a scrollable
		  region. WCAG 2.1.1 (Keyboard) requires scrollable regions to be
		  keyboard-focusable so keyboard-only users can scroll them, and 4.1.2
		  requires an accessible name. We expose it as a labelled region and
		  give it tabindex="0". When the header (and its title) is hidden we
		  fall back to an explicit aria-label from the widget title so the
		  region is never anonymous.
		-->
		<div
			class="cn-widget-wrapper__content"
			tabindex="0"
			role="region"
			:aria-labelledby="titleVisible ? titleId : null"
			:aria-label="titleVisible ? null : resolvedTitle">
			<slot />
		</div>

		<!-- Footer -->
		<div v-if="$slots.footer || (buttons && buttons.length > 0)" class="cn-widget-wrapper__footer">
			<slot name="footer">
				<a
					v-for="button in buttons"
					:key="button.link"
					:href="button.link"
					class="cn-widget-wrapper__footer-link">
					{{ button.text }}
				</a>
			</slot>
		</div>
	</div>
</template>

<script>
import { translate as t } from '@nextcloud/l10n'
import { slotRenders } from '../../utils/slotContent.js'
import { CnActionsMenu } from '../CnActionsMenu/index.js'

/**
 * CnWidgetWrapper — Widget container with header, content, and footer.
 *
 * ```vue
 * <CnWidgetWrapper title="My Cases" :icon-url="casesIconUrl">
 *   <MyCasesChart :data="chartData" />
 * </CnWidgetWrapper>
 * ```
 *
 * With NC widget object
 * ```vue
 * <CnWidgetWrapper
 *   :title="widget.title"
 *   :icon-url="widget.iconUrl"
 *   :icon-class="widget.iconClass"
 *   :documentation-url="widget.documentationUrl"
 *   :buttons="widget.buttons">
 *   <CnWidgetRenderer :widget="widget" />
 * </CnWidgetWrapper>
 * ```
 */
/**
 * Semantic variant → CSS colour token for the widget header icon. Tokens
 * only — the nldesign app themes by overriding the Nextcloud variables, so a
 * literal hex here would ignore government theming entirely (ADR-062 / NL
 * Design System).
 *
 * @type {Record<string, string>}
 */
const TITLE_ICON_VARIANT_COLORS = {
	primary: 'var(--color-primary-element)',
	success: 'var(--color-success)',
	warning: 'var(--color-warning)',
	error: 'var(--color-error)',
	info: 'var(--color-info, var(--color-primary-element))',
	neutral: 'var(--color-text-maxcontrast)',
}

export default {
	name: 'CnWidgetWrapper',

	components: {
		CnActionsMenu,
	},

	inject: {
		/**
		 * Host translate function provided by CnAppRoot as
		 * `cnTranslate: this.translate` (bound to the host app's id). The
		 * manifest-authored widget title is run through it so the card
		 * heading follows the user's language. Defaults to an identity
		 * function so an untranslated key renders as itself.
		 */
		cnTranslate: { default: () => (key) => key },
	},

	props: {
		/** Widget title */
		title: {
			type: String,
			default: () => t('nextcloud-vue', 'Widget'),
		},

		/** Whether to show the header with title */
		showTitle: {
			type: Boolean,
			default: true,
		},

		/**
		 * Chrome variant for the wrapper card.
		 * - `'default'` — the library's own card chrome (opaque background,
		 *   1px border, compact header).
		 * - `'nc-dashboard'` — matches the native Nextcloud Dashboard panel
		 *   exactly using the same design tokens: translucent blurred
		 *   background (`--color-main-background-blur` + `--filter-background-blur`),
		 *   `--border-radius-container-large` corners, no border/shadow, a 16px
		 *   header with a 20px/700 title and 32px leading icon, and content
		 *   inset 16px on the sides + bottom. `styleConfig` overrides still
		 *   layer on top so a user can customise any token.
		 *
		 * @type {'default'|'nc-dashboard'}
		 */
		chrome: {
			type: String,
			default: 'default',
			validator: (v) => ['default', 'nc-dashboard'].includes(v),
		},

		/**
		 * Remove border and background — makes the wrapper transparent.
		 * Useful for widgets that are self-contained cards (e.g. CnStatsBlock).
		 */
		borderless: {
			type: Boolean,
			default: false,
		},

		/**
		 * Remove content padding — allows content to go edge-to-edge.
		 * Useful for list-style widgets where items should span the full width.
		 */
		flush: {
			type: Boolean,
			default: false,
		},

		/**
		 * Draw NO card of this widget's own: no border, no background, no
		 * content padding, no title row and no header divider. For a surface
		 * that already supplies the card and the heading — a tab panel being
		 * the case that matters, where a nested card repeats both.
		 *
		 * The wrapper ELEMENT still renders. That is the contract, not an
		 * oversight: `CnObjectDataWidget` measures its overflow against
		 * `.cn-widget-wrapper__content` (`closest()` plus a ResizeObserver on
		 * that node), `src/css/table.css` gives a table inside it its
		 * edge-to-edge treatment, and `src/css/detail-page.css` and
		 * `dashboard.css` size their content areas through it. Dropping the
		 * node would take all of that out silently — `closest()` returning
		 * null reads as "nothing overflows", never as an error.
		 *
		 * Controls in the `actions` slot are NOT chrome and keep their header:
		 * the Save button that commits an inline edit lives there, and a panel
		 * that hides it is silently unsaveable.
		 *
		 * `chromeless` is what an integration provider already asks for through
		 * `CnDetailWidgetHost` (`bareWidget`), so this is the same word for the
		 * same thing on every path into the wrapper.
		 */
		chromeless: {
			type: Boolean,
			default: false,
		},

		/** Icon URL (image) */
		iconUrl: {
			type: String,
			default: null,
		},

		/** Icon CSS class (e.g., Nextcloud icon class) */
		iconClass: {
			type: String,
			default: null,
		},

		/**
		 * Position of the title-icon slot in the header.
		 * 'left' places it before the title; 'right' places it after the actions.
		 */
		titleIconPosition: {
			type: String,
			default: 'right',
			validator: (v) => ['left', 'right'].includes(v),
		},

		/**
		 * Explicit CSS colour for the header icon. Overrides `titleIconVariant`.
		 * Must be a CSS custom property or a theme token — never a literal hex
		 * (NL Design System: the nldesign app re-themes by overriding the
		 * Nextcloud variables, and a hardcoded colour ignores that).
		 *
		 * @type {string}
		 */
		titleIconColor: {
			type: String,
			default: null,
		},

		/**
		 * Semantic colour for the header icon. Every widget's icon is
		 * coloured — `primary` (the theme colour) is the default, and a
		 * widget whose subject already carries a semantic meaning names it
		 * here so the icon says so at a glance (a Depublished list is
		 * `error`, a Concepts list `warning`, a Published list `success`).
		 *
		 * @type {'primary'|'success'|'warning'|'error'|'info'|'neutral'}
		 */
		titleIconVariant: {
			type: String,
			default: 'primary',
			validator: (v) => ['primary', 'success', 'warning', 'error', 'info', 'neutral'].includes(v),
		},

		/** Footer action buttons: [{ text, link }] */
		buttons: {
			type: Array,
			default: () => [],
		},

		/**
		 * Whether the header's overflow action menu (Refresh / Documentation /
		 * Request-a-feature + any `#action-items`) renders. Shown by default;
		 * set `false` for compact surfaces — e.g. a KPI tile whose only header
		 * affordance is a date chip — to drop the menu and free header width.
		 */
		showActions: {
			type: Boolean,
			default: true,
		},

		/**
		 * Style configuration for the wrapper.
		 *
		 * @type {{ backgroundColor: string, borderStyle: string, borderWidth: number, borderColor: string, borderRadius: number, padding: { top: number, right: number, bottom: number, left: number } }}
		 */
		styleConfig: {
			type: Object,
			default: () => ({}),
		},

		/**
		 * Hide the built-in Refresh item from the overflow action menu.
		 * The Refresh item is shown by default — set this when the widget
		 * has no refreshable data source (e.g. a static tile). Alias for
		 * the inverse `:show-refresh="false"`; either form opts out.
		 */
		hideRefresh: {
			type: Boolean,
			default: false,
		},

		/**
		 * Hide the built-in Request-a-feature item from the overflow
		 * action menu. Shown by default; set when the consuming app has
		 * no public issue tracker to link out to. Alias for the inverse
		 * `:show-request-feature="false"`; either form opts out.
		 */
		hideRequestFeature: {
			type: Boolean,
			default: false,
		},

		/**
		 * Whether to show the built-in Refresh item. Tri-state:
		 * - `true` / `false` — force the action on or off.
		 * - `null` (the default) — **auto**: show the action only when a
		 *   parent has attached an `@refresh` listener (i.e. something will
		 *   actually handle the refresh). This keeps widgets that can't
		 *   refresh — e.g. a prop-driven `CnObjectDataWidget` — from showing
		 *   a dead button. Widgets that refresh themselves via the
		 *   `cn:widget:refresh` event bus (with no `@refresh` listener)
		 *   should set `:show-refresh="true"` explicitly.
		 *
		 * `hideRefresh` (or `:show-refresh="false"`) always wins.
		 *
		 * @type {boolean|null}
		 */
		showRefresh: {
			type: Boolean,
			default: null,
		},

		/**
		 * Inverse of `hideRequestFeature`. Defaults to `true` so the
		 * action renders. Set `:show-request-feature="false"` to hide it.
		 * Either flag hides the action.
		 *
		 * @type {boolean}
		 */
		showRequestFeature: {
			type: Boolean,
			default: true,
		},

		/**
		 * Whether the built-in "Report a bug" item renders. On by default —
		 * the trio Request a feature / Report a bug / Documentation is the
		 * contract for every widget; this exists for the rare surface that
		 * must suppress one deliberately.
		 *
		 * @type {boolean}
		 */
		showReportBug: {
			type: Boolean,
			default: true,
		},

		/**
		 * Whether the built-in "Documentation" item renders. On by default,
		 * for the same reason as `showReportBug`. The item's target is
		 * resolved by the shared menu (see `docsAnchor`), so leaving it on
		 * costs the host nothing.
		 *
		 * @type {boolean}
		 */
		showDocumentation: {
			type: Boolean,
			default: true,
		},

		/**
		 * Explicit documentation link for this widget, opened in a new tab.
		 * Usually unnecessary: leave it empty and set `docsAnchor` instead,
		 * so the link is built from the app-wide documentation base and
		 * lands on this widget's own section.
		 *
		 * @type {string}
		 */
		documentationUrl: {
			type: String,
			default: '',
		},

		/**
		 * Optional pre-translated label for the Documentation action.
		 * Defaults to the lib's translation of "Documentation".
		 */
		documentationLabel: {
			type: String,
			default: () => t('nextcloud-vue', 'Documentation'),
		},

		/**
		 * Widget id for the built-in default Refresh / Request-a-feature
		 * handlers (B2). Forwarded as the `surface: "widget:<id>"` value
		 * on the Actions menu (the English headline fallback for its forge
		 * deep-links) AND as the `widgetId` field on the
		 * `cn:widget:refresh` event-bus payload. When unset, the wrapper
		 * falls back to a slugified `displayTitle`, which works but is
		 * less stable than an explicit id.
		 *
		 * @type {string}
		 */
		widgetId: {
			type: String,
			default: '',
		},

		/**
		 * This widget's own section in the app's documentation, appended to
		 * the app-wide documentation base URL (provided by CnAppRoot) to build
		 * the Actions menu's Documentation deep-link. A bare slug becomes a
		 * `#fragment`. Supply it per widget type — without it the item still
		 * renders, but lands on the docs homepage rather than this widget.
		 *
		 * @type {string}
		 */
		docsAnchor: {
			type: String,
			default: '',
		},

		/**
		 * Explicit "Report a bug" target for the Actions menu. Empty (the
		 * default) builds a new-issue deep-link on the app's own forge.
		 *
		 * @type {string}
		 */
		reportBugUrl: {
			type: String,
			default: '',
		},

		/**
		 * Optional `specRef` slug, forwarded to the Actions menu. Accepted
		 * for backward compatibility with the removed in-product suggestion
		 * modal; the forge issue form asks for its own context.
		 *
		 * @type {string}
		 */
		specRef: {
			type: String,
			default: '',
		},

		/**
		 * Whether a refresh is currently in flight. When bound by the host
		 * (e.g. `:refreshing="loading"` around its refetch), the Refresh
		 * item is disabled and shows a loading spinner for exactly as long
		 * as this stays true — so the spinner reflects the real refresh time.
		 *
		 * @type {boolean}
		 */
		refreshing: {
			type: Boolean,
			default: false,
		},

		/**
		 * Optional pre-translated label for the Refresh action. Defaults
		 * to the lib's translation of "Refresh" so callers usually don't
		 * need to set this.
		 */
		refreshLabel: {
			type: String,
			default: () => t('nextcloud-vue', 'Refresh'),
		},

		/**
		 * Optional pre-translated label for the Request-a-feature action.
		 * Defaults to the lib's translation of "Request a feature".
		 */
		requestFeatureLabel: {
			type: String,
			default: () => t('nextcloud-vue', 'Request a feature'),
		},

		/**
		 * Pre-translated aria-label / tooltip for the overflow menu
		 * trigger. Defaults to "Actions".
		 */
		actionsMenuLabel: {
			type: String,
			default: () => t('nextcloud-vue', 'Actions'),
		},

		/**
		 * Translate function. Falls back to the injected `cnTranslate`,
		 * which itself defaults to an identity function.
		 *
		 * @type {((key: string) => string)|null}
		 */
		translate: {
			type: Function,
			default: null,
		},
	},

	emits: ['refresh', 'request-feature'],

	computed: {
		displayTitle() {
			return this.title || 'Widget'
		},

		/**
		 * The header's icon colour, published as ONE custom property on the
		 * header rather than an inline `color` repeated on each icon element.
		 * Everything under the header — the icon-class span, the `title-icon`
		 * slot's content, an SVG inside it — inherits from that single
		 * declaration, so the icon and any decoration around it cannot end up
		 * different colours.
		 *
		 * `titleIconColor` wins when the host set one; otherwise the
		 * `titleIconVariant` token does, which is why EVERY widget icon is
		 * coloured and not just the ones an app remembered to configure. Every
		 * value is a CSS variable, so the nldesign app's overrides re-theme
		 * them for free (NL Design System).
		 *
		 * @return {object} A style object declaring `--cn-widget-icon-color`.
		 */
		titleIconStyle() {
			return {
				'--cn-widget-icon-color': this.titleIconColor
					|| TITLE_ICON_VARIANT_COLORS[this.titleIconVariant]
					|| TITLE_ICON_VARIANT_COLORS.primary,
			}
		},

		/**
		 * Effective translate function: the explicit `translate` prop when
		 * given, else the injected `cnTranslate` (identity by default).
		 *
		 * @return {(key: string) => string}
		 */
		effectiveTranslate() {
			return this.translate ?? this.cnTranslate
		},

		/**
		 * The RENDERED widget title — `displayTitle` run through the host
		 * translate function. Kept separate from `displayTitle` on purpose:
		 * `resolvedWidgetId` slugifies the raw title, so identifiers (DOM
		 * ids, refresh channels, action payloads) stay locale-independent
		 * while only the visible/AT-facing text localises.
		 *
		 * @return {string}
		 */
		resolvedTitle() {
			return this.title ? this.effectiveTranslate(this.title) : this.displayTitle
		},

		/**
		 * Stable DOM id for the header title `<h3>`, used as the
		 * `aria-labelledby` target of the scrollable content region so the
		 * region gets an accessible name from the widget title (WCAG 4.1.2).
		 * Derived from `resolvedWidgetId` so it is stable and unique per
		 * widget instance on the page.
		 *
		 * @return {string}
		 */
		titleId() {
			return `cn-widget-wrapper-title-${this.resolvedWidgetId}`
		},

		/**
		 * Effective Refresh visibility. `hideRefresh: true` (or
		 * `:show-refresh="false"`) always hides it. When `showRefresh` is
		 * left unset (`null`), auto-detect: show the action only when a
		 * parent attached an `@refresh` listener — otherwise the refresh
		 * would do nothing. `hideRefresh` remains a back-compat alias.
		 *
		 * @return {boolean}
		 */
		effectiveShowRefresh() {
			if (this.hideRefresh) {
				return false
			}
			if (this.showRefresh !== null) {
				return this.showRefresh
			}
			// `$.vnode.props`, not `$attrs`: `refresh` is a declared emit, and
			// Vue keeps declared emits out of `$attrs`.
			return Boolean(this.$.vnode.props?.onRefresh)
		},

		/**
		 * Effective Request-a-feature visibility — same OR-of-opt-outs
		 * pattern as `effectiveShowRefresh`.
		 *
		 * @return {boolean}
		 */
		effectiveShowRequestFeature() {
			return this.showRequestFeature && !this.hideRequestFeature
		},

		/**
		 * Stable id used in event-bus payloads and the
		 * `surface: "widget:<id>"` field on the auto-mounted feature
		 * modal. Prefers the explicit `widgetId` prop; falls back to a
		 * slugified `displayTitle` so widgets without an explicit id
		 * still get a usable identifier.
		 *
		 * @return {string}
		 */
		resolvedWidgetId() {
			if (this.widgetId) {
				return this.widgetId
			}
			return this.displayTitle.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
		},

		/**
		 * Whether the caller supplied an `action-items` slot. Forwarded
		 * conditionally so the shared CnActionsMenu doesn't treat an
		 * always-present pass-through template as content (which would
		 * keep the overflow menu visible even when every built-in is
		 * opted out).
		 *
		 * @return {boolean}
		 */
		hasActionItemsSlot() {
			return Boolean(this.$slots['action-items']) || Boolean(this.$slots && this.$slots['action-items'])
		},

		/**
		 * Whether the caller put controls in the `actions` slot.
		 *
		 * Gates the header together with `showTitle`, so a surface that wants no
		 * title still keeps controls that have nowhere else to go — the Save
		 * button `CnObjectDataWidget` renders there being the case that matters.
		 *
		 * Deliberately NOT `showActions`: that prop defaults to true and governs
		 * the overflow menu, so reading it here would give a header to every
		 * headerless KPI tile that never had one.
		 *
		 * @return {boolean} true when the slot is filled.
		 */
		hasActionsSlot() {
			return Boolean(this.$slots.actions)
		},

		/**
		 * Whether the card outline is drawn. `chromeless` implies `borderless`
		 * so a caller names the intent once instead of remembering the three
		 * props it decomposes into.
		 *
		 * @return {boolean} true when border and background are suppressed.
		 */
		noBorder() {
			return this.borderless || this.chromeless
		},

		/**
		 * Whether the content area goes edge to edge. `chromeless` implies
		 * `flush` for the same reason as `noBorder`.
		 *
		 * @return {boolean} true when content padding is suppressed.
		 */
		noPadding() {
			return this.flush || this.chromeless
		},

		/**
		 * Whether the title row renders. `chromeless` overrides `showTitle`,
		 * which defaults to true: a surface that already names the widget must
		 * not have to also remember to turn the title off.
		 *
		 * Read everywhere the template used to read `showTitle` directly,
		 * including the two ARIA bindings on the content region — a hidden
		 * title cannot be the region's `aria-labelledby` target.
		 *
		 * @return {boolean} true when the header's title group renders.
		 */
		titleVisible() {
			return this.showTitle && !this.chromeless
		},

		wrapperStyles() {
			const styles = {}

			if (this.styleConfig.backgroundColor) {
				styles.backgroundColor = this.styleConfig.backgroundColor
			}

			if (this.styleConfig.borderStyle && this.styleConfig.borderStyle !== 'none') {
				styles.border = `${this.styleConfig.borderWidth || 1}px ${this.styleConfig.borderStyle} ${this.styleConfig.borderColor || 'var(--color-border)'}`
			}

			if (this.styleConfig.borderRadius !== undefined) {
				styles.borderRadius = `${this.styleConfig.borderRadius}px`
			}

			if (this.styleConfig.padding) {
				const p = this.styleConfig.padding
				styles.padding = `${p.top || 0}px ${p.right || 0}px ${p.bottom || 0}px ${p.left || 0}px`
			}

			return styles
		},

		/**
		 * Inline styles for the header bar, derived from
		 * `styleConfig.headerStyle.{backgroundColor, textColor}`. Lets a host
		 * give individual widgets a custom header colour without a per-app CSS
		 * workaround. Empty object when no header style is configured.
		 *
		 * @return {object} the header style bindings.
		 */
		headerStyles() {
			const hs = this.styleConfig.headerStyle
			if (!hs || typeof hs !== 'object') {
				return {}
			}
			const styles = {}
			if (hs.backgroundColor) {
				styles.backgroundColor = hs.backgroundColor
			}
			if (hs.textColor) {
				styles.color = hs.textColor
			}
			return styles
		},
	},

	methods: {
		/**
		 * Whether the `actions` slot renders anything a reader can see.
		 *
		 * `CnObjectDataWidget` always PROVIDES the template and fills it only
		 * while an edit is unsaved, so the slot has to be called and its vnodes
		 * inspected. Reading `$slots.actions` as a boolean counts the template
		 * itself as content, which is what gave a titleless tab panel a 59px
		 * band holding nothing but a second Actions menu.
		 *
		 * A method rather than a computed on purpose. The answer changes when the
		 * PARENT's state changes, and a computed reading `$slots` does not
		 * re-track that; a method re-runs on every render, and a slot whose
		 * content depends on parent state already forces this component to
		 * re-render.
		 *
		 * @return {boolean} True when the slot yields visible content.
		 */
		actionsSlotRenders() {
			return slotRenders(this.$slots.actions)
		},

		/**
		 * Whether the header band is worth the vertical space it costs.
		 *
		 * A VISIBLE title always earns it, which `chromeless` rules out. Otherwise
		 * it is earned by controls that are actually on screen: content in the
		 * `actions` slot, or the overflow menu when a caller both asked for it and
		 * provided the slot it sits beside.
		 *
		 * That last clause looks redundant and is not. `showActions` defaults to
		 * TRUE, so reading it alone hands a header to every headerless KPI tile
		 * in the fleet, which is what the floating title-meta test caught when
		 * this was first written the short way.
		 *
		 * @return {boolean} True when the header should render.
		 */
		headerIsWorthIt() {
			if (this.titleVisible) {
				return true
			}
			if (this.actionsSlotRenders()) {
				return true
			}
			return this.showActions && this.hasActionsSlot
		},

		/**
		 * Whether the actions group holds anything.
		 *
		 * The overflow menu and the slot beside it are separate conditions now.
		 * `showActions` used to gate both, so a caller who only wanted the menu
		 * gone also lost the button that commits an inline edit.
		 *
		 * @return {boolean} True when the group would hold a visible control.
		 */
		actionsAreVisible() {
			return this.showActions || this.actionsSlotRenders()
		},

		/**
		 * Re-emit the shared CnActionsMenu `@refresh` to the host, passing
		 * the synthetic event through unchanged so a host listener can
		 * still `preventDefault()` the built-in default (event-bus emit on
		 * `cn:widget:refresh`).
		 *
		 * @param {{ widgetId: string, title: string }} payload Action payload.
		 * @param {{ defaultPrevented: boolean, preventDefault: () => void }} ev Synthetic event.
		 * @return {void}
		 */
		onActionsRefresh(payload, ev) {
			/**
			 * @event refresh User clicked the Refresh item in the overflow
			 * action menu. Payload: `{ widgetId, title }`. Handlers may call
			 * the second arg's `preventDefault()` to suppress the built-in
			 * default (event-bus emit on `cn:widget:refresh`).
			 * @type {{ widgetId: string, title: string }}
			 */
			this.$emit('refresh', payload, ev)
		},

		/**
		 * Re-emit the shared CnActionsMenu `@request-feature` to the host,
		 * passing the synthetic event through so a host listener can
		 * `preventDefault()` the built-in default (opening the forge's
		 * feature-request issue form).
		 *
		 * @param {{ widgetId: string, title: string }} payload Action payload.
		 * @param {{ defaultPrevented: boolean, preventDefault: () => void }} ev Synthetic event.
		 * @return {void}
		 */
		onActionsRequestFeature(payload, ev) {
			/**
			 * @event request-feature User clicked the Request a feature
			 * item. Payload: `{ widgetId, title }`. Handlers may call the
			 * second arg's `preventDefault()` to suppress the built-in
			 * default (opening the forge's feature-request issue form).
			 * @type {{ widgetId: string, title: string }}
			 */
			this.$emit('request-feature', payload, ev)
		},
	},
}
</script>

<style scoped>
.cn-widget-wrapper {
	position: relative;
	height: 100%;
	display: flex;
	flex-direction: column;
	background: var(--color-main-background);
	border: 1px solid var(--color-border);
	/* NC design system: every content card is rounded — same token as
	   CnDetailCard so detail pages read as ONE card family (ADR-062). */
	border-radius: var(--border-radius-large, 8px);
	overflow: hidden;
}

/* Headerless date chip — floated over the top-right of a flush card. */
.cn-widget-wrapper__floating-meta {
	position: absolute;
	top: 8px;
	inset-inline-end: 10px;
	z-index: 2;
}

.cn-widget-wrapper__content {
	flex: 1;
	overflow: auto;
	min-height: 0;
	padding: 16px;
}

/* A TABLE GOES EDGE TO EDGE. ALWAYS.
   A table drawn inside 16px of padding renders a border inside a border — two
   rectangles a few pixels apart, which reads as a mistake because it is one.
   The wrapper already has a `flush` prop that zeroes this padding, but a prop
   is something an app has to remember on every table it ever adds, and the
   apps did not: the padding was visible in pipelinq's table widgets.
   So it is structural instead. `:has()` keys on the table component's own root
   class, which means a table is flush by construction and no caller can
   reintroduce the gap by forgetting a prop. */
.cn-widget-wrapper__content:has(> .cn-table-container),
.cn-widget-wrapper__content:has(> .cn-widget-object-table) {
	padding: 0;
}

/* Keyboard focus ring for the now-focusable scrollable content region. */
.cn-widget-wrapper__content:focus-visible {
	outline: 2px solid var(--color-primary-element);
	outline-offset: -2px;
}

.cn-widget-wrapper--borderless {
	border: none;
	background: transparent;
}

.cn-widget-wrapper--borderless .cn-widget-wrapper__content {
	padding: 0;
}

.cn-widget-wrapper--flush .cn-widget-wrapper__content {
	padding: 0;
}

/* `chromeless` — border and background are already gone through borderless,
   and the content padding through flush. What is left on the root is the
   corner radius, which still clips: with `overflow: hidden` above, a child
   that paints its own background (a table header) gets rounded corners inside
   a square panel, which is the last visible piece of a card that is supposed
   to be absent. `CnDetailCard.cn-detail-card--chromeless` zeroes the same
   thing for the same reason. */
.cn-widget-wrapper--chromeless {
	border-radius: 0;
}

/*
 * `chrome="nc-dashboard"` — reproduce the native Nextcloud Dashboard panel
 * (apps/dashboard) exactly, using the same design tokens so an un-customised
 * widget is pixel-identical to a core dashboard panel. `styleConfig` inline
 * overrides (wrapperStyles / headerStyles) still win over these class rules,
 * so users can override any token. Combined selector raises specificity above
 * the base `.cn-widget-wrapper` rules it supersedes.
 */
.cn-widget-wrapper__header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	/* The header is `space-between`, so without an explicit gap a right-hand
	   title-icon sits flush against whatever precedes it. The left-hand icon's
	   spacing comes from header-left's own gap. */
	gap: 8px;
	padding: 12px 16px;
	border-bottom: 1px solid var(--color-border);
	flex-shrink: 0;
}

/* Actions with no title beside them. `header-left` is not rendered at all in
   this mode, and the header is `space-between`, so a single child would sit
   flush LEFT where every other surface puts its actions on the right. */
.cn-widget-wrapper__header--actions-only {
	justify-content: flex-end;
}

/* `chromeless` — the surface around this widget already drew the card, so
   nothing here may draw a second one. Border, background and content padding
   are already gone (chromeless implies borderless + flush); what is left is
   the header, which still renders when there are controls to hold. Its bottom
   rule is the card's own divider and reads as a second card edge inside the
   panel, and its 16px side inset leaves those controls hanging off the grid
   that now starts at the panel's edge. Both go.

   Placed AFTER the rules it overrides, which is what stylelint's
   no-descending-specificity asks for. The cascade did not need it (this
   selector is more specific either way), a reader does: an override written
   above the thing it overrides has to be read twice. */
.cn-widget-wrapper--chromeless .cn-widget-wrapper__header {
	border-bottom: none;
	padding-inline: 0;
}

.cn-widget-wrapper__header-left {
	display: flex;
	align-items: center;
	gap: 8px;
	min-width: 0;
	/* Grow so the title hugs the (optional) title-icon on the left instead
	   of centering between icon and actions (header is space-between). */
	flex: 1 1 auto;
}

.cn-widget-wrapper__icon {
	width: 24px;
	height: 24px;
	flex-shrink: 0;
	color: var(--cn-widget-icon-color, var(--color-primary-element));
}

.cn-widget-wrapper__title {
	font-weight: 600;
	font-size: 14px;
	margin: 0;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.cn-widget-wrapper.cn-widget-wrapper--nc-dashboard {
	background: var(--color-main-background-blur, var(--color-main-background));
	-webkit-backdrop-filter: var(--filter-background-blur, none);
	backdrop-filter: var(--filter-background-blur, none);
	border: none;
	border-radius: var(--border-radius-container-large, 16px);
}

.cn-widget-wrapper--nc-dashboard .cn-widget-wrapper__header {
	padding: 16px;
	border-bottom: none;
}

.cn-widget-wrapper--nc-dashboard .cn-widget-wrapper__header-left {
	gap: 16px;
}

.cn-widget-wrapper--nc-dashboard .cn-widget-wrapper__icon {
	width: 32px;
	height: 32px;
	background-size: 32px;
}

.cn-widget-wrapper--nc-dashboard .cn-widget-wrapper__title {
	font-size: 20px;
	font-weight: 700;
	line-height: 24px;
}

.cn-widget-wrapper--nc-dashboard .cn-widget-wrapper__content {
	padding: 0 16px 16px;
}

.cn-widget-wrapper__title-meta {
	display: flex;
	align-items: center;
	gap: 4px;
	font-size: 12px;
	color: var(--color-text-maxcontrast);
	white-space: nowrap;
	flex-shrink: 0;
}

.cn-widget-wrapper__actions {
	display: flex;
	gap: 4px;
	flex-shrink: 0;
}

/* Every widget's header icon is coloured — `--cn-widget-icon-color` is set on
   the header from titleIconStyle, and defaults to the theme colour if a
   wrapper is ever rendered without it. Material-design-icon components paint
   with `fill: currentColor`, so this reaches them; the :deep rule below makes
   it reach a raw <svg> in the slot too. */
.cn-widget-wrapper__title-icon {
	display: flex;
	align-items: center;
	flex-shrink: 0;
	/* COLOURED BY DEFAULT. `--cn-widget-icon-color` is set on the header from
	   titleIconStyle; the theme colour is the fallback for a wrapper rendered
	   without it. Before this, the prop defaulted to null and the icon inherited
	   body text colour, which is why every widget header looked the same. */
	color: var(--cn-widget-icon-color, var(--color-primary-element));

	/* SPACE BETWEEN THE ICON AND THE TITLE.
	   This element is a sibling of `__header-left` inside a space-between
	   header, so it never received that element's `gap: 8px` — the icon sat
	   flush against the title text. Matching the header-left gap rather than
	   inventing a value keeps the two icon positions spaced identically. */
	margin-inline-end: 8px;
}

/* Right-positioned title icons sit AFTER the actions, so the margin belongs on
   the other side or it pushes the icon off the header's right edge. */
.cn-widget-wrapper__title-icon:last-child {
	margin-inline-end: 0;
	margin-inline-start: 8px;
}

/* Material-design-icon components paint with `fill: currentColor`, so the
   colour above reaches them; this makes it reach a raw <svg> in the slot too. */
.cn-widget-wrapper__title-icon :deep(svg) {
	fill: currentcolor;
}

.cn-widget-wrapper__footer {
	display: flex;
	justify-content: flex-end;
	gap: 8px;
	padding: 8px 16px;
	border-top: 1px solid var(--color-border);
	flex-shrink: 0;
}

.cn-widget-wrapper__footer-link {
	font-size: 13px;
	color: var(--color-primary-element);
	text-decoration: none;
}

.cn-widget-wrapper__footer-link:hover {
	text-decoration: underline;
}
</style>
